import React from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Trash2 } from "lucide-react";

/**
 * A reusable confirmation dialog component.
 * 
 * @param {boolean} isOpen - Controls the open state
 * @param {function} onOpenChange - Callback when open state changes 
 * @param {function} onConfirm - Callback when user confirms the action
 * @param {string} title - The dialog title message
 * @param {string} description - The dialog description message
 * @param {string} confirmText - The text for the confirm button (default: 'Confirm')
 * @param {string} cancelText - The text for the cancel button (default: 'Cancel')
 * @param {boolean} isDestructive - If true, styles the button red and shows a warning icon
 * @param {boolean} isLoading - Shows loading state on the confirm button if true
 */
const ConfirmDialog = ({
    isOpen,
    onOpenChange,
    onConfirm,
    title = "Are you sure?",
    description = "This action cannot be undone.",
    confirmText = "Confirm",
    cancelText = "Cancel",
    isDestructive = true,
    isLoading = false,
}) => {
    return (
        <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        {isDestructive && <AlertTriangle className="h-5 w-5 text-red-500" />}
                        {title}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {description}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isLoading}>{cancelText}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            onConfirm();
                        }}
                        disabled={isLoading}
                        className={isDestructive ? "bg-red-600 hover:bg-red-700 text-white focus:ring-red-600" : ""}
                    >
                        {isLoading ? "Processing..." : (
                            <>
                                {isDestructive && <Trash2 className="w-4 h-4 mr-2" />}
                                {confirmText}
                            </>
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default ConfirmDialog;
